import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://lamko.co.kr';
const DEFAULT_OG_IMAGE = `${SITE_URL}/office-1.jpg`;

/**
 * Per-page SEO component. Renders into <head> via react-helmet-async.
 * Provides unique title, description, canonical, OpenGraph, and an optional
 * Korean description for Naver.
 */
export const SEO = ({
  title,
  description,
  descriptionKo,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  lang = 'en',
}) => {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title
    ? `${title} | LAMKO`
    : 'LAMKO Co., Ltd. - High-Performance Materials';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {descriptionKo && (
        <meta name="description" lang="ko" content={descriptionKo} />
      )}
      <link rel="canonical" href={url} />
      <link rel="alternate" href={url} hrefLang={lang} />
      <link rel="alternate" href={url} hrefLang="x-default" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="LAMKO Co., Ltd." />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === 'ko' ? 'ko_KR' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

/**
 * Site-wide Organization JSON-LD. Rendered once at App level so every
 * prerendered page contains the same Organization markup in <head>.
 */
export const OrganizationJsonLd = () => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Lumora Advanced Materials Korea (LAMKO)',
    alternateName: 'LAMKO Co., Ltd.',
    url: SITE_URL,
    logo: `${SITE_URL}/images/lamko-logo.png`,
    email: 'info@lamko.co.kr',
    telephone: '+82-10-7269-6809',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '3F, 122, Mapo-daero',
      addressLocality: 'Mapo-gu',
      addressRegion: 'Seoul',
      postalCode: '04213',
      addressCountry: 'KR',
    },
    sameAs: [
      'https://www.linkedin.com/company/lamko',
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};
